// Extrait du proto Modules/12M Cash/treso_v9.jsx (lignes 618-922).
// VERBATIM — moteur principal qui projette la trésorerie sur l'horizon mensuel.

import { sum, calToHorizon } from './helpers';
import { computeMensuelValeurs, computeSpecifiqueValeurs } from './computeMonthlyValues';
import { computeVariableValeurs } from './computeVariableValues';
import { calcSalaires } from './computeSalaries';

export function calculerScenario(s) {
  const H = s.horizon || 12;
  const months = Array.from({ length: H }, (_, i) => i);
  const safeArr = (v) => Array.isArray(v) ? v : Array(H).fill(0);

  // 1. ENCAISSEMENTS (revenus HT + TVA si soumis = TTC)
  // Taux TVA par mois (year-aware) si fourni, sinon scalaire (fixtures/tests).
  // Le taux est celui du mois de l'opération (date de la prestation/facturation).
  const tauxTvaPct = (Number(s.tauxTva) || 0) / 100;
  const tvaByMonth = Array.isArray(s.tauxTvaPctByMonth) ? s.tauxTvaPctByMonth : null;
  const tvaRateAt = (m) => (tvaByMonth ? (Number(tvaByMonth[m]) || 0) / 100 : tauxTvaPct);
  const moisDebut = Number.isInteger(s.moisDebut) ? s.moisDebut : 0;
  const encaisseHT = Array(H).fill(0);
  const encaisseTVA = Array(H).fill(0);
  (s.revenus || []).forEach(l => {
    safeArr(l.valeurs).forEach((val, m) => {
      const mDest = m + (l.delai || 0);
      const ht = Number(val) || 0;
      const tva = l.soumisTva ? ht * tvaRateAt(m) : 0;
      if (mDest < H) {
        encaisseHT[mDest] += ht;
        encaisseTVA[mDest] += tva;
      }
    });
  });

  // Opening receivables : amount × pct[calendar] applied at the scenario
  // month corresponding to that calendar slot. No VAT (already collected
  // before the plan starts — they are pure cash-in).
  const encaisseOpening = Array(H).fill(0);
  (s.poCreancesLines || []).forEach(line => {
    const amt = Number(line.amount) || 0;
    const pcts = line.collectionPct || [];
    for (let m = 0; m < H; m += 1) {
      const calIdx = (moisDebut + m) % 12;
      const p = Number(pcts[calIdx]) || 0;
      encaisseOpening[m] += amt * p / 100;
    }
  });

  const encaisseTTC = months.map(m => encaisseHT[m] + encaisseTVA[m] + encaisseOpening[m]);

  // 2. DÉCAISSEMENTS EXPLOITATION
  const variablesAvecValeurs = (s.variables || []).map(c => ({ ...c, valeurs: computeVariableValeurs(c, s.revenus || []) }));
  const mensuelAvecValeurs = (s.mensuel || []).map(c => ({ ...c, valeurs: computeMensuelValeurs(c) }));
  const specifiqueAvecValeurs = (s.specifique || []).map(c => ({ ...c, valeurs: computeSpecifiqueValeurs(c) }));

  const decaisseHTPar = (lignes, applyDelai = true) => {
    const ht = Array(H).fill(0);
    const tva = Array(H).fill(0);
    lignes.forEach(c => {
      safeArr(c.valeurs).forEach((v, m) => {
        const mDest = applyDelai ? m + (c.delai || 0) : m;
        const valHT = Number(v) || 0;
        const valTva = c.soumisTva ? valHT * tvaRateAt(m) : 0;
        if (mDest < H) {
          ht[mDest] += valHT;
          tva[mDest] += valTva;
        }
      });
    });
    return { ht, tva };
  };

  const variables = decaisseHTPar(variablesAvecValeurs);
  const mensuel = decaisseHTPar(mensuelAvecValeurs, false);
  const specifique = decaisseHTPar(specifiqueAvecValeurs, false);

  const decaisseExploitHT = months.map(m => variables.ht[m] + mensuel.ht[m] + specifique.ht[m]);
  const decaisseExploitTVA = months.map(m => variables.tva[m] + mensuel.tva[m] + specifique.tva[m]);

  // Opening payables : amount × pct[calendar] paid at the scenario month
  // corresponding to that calendar slot. No VAT split (already accounted
  // for before the plan started).
  const decaisseOpening = Array(H).fill(0);
  (s.poDettesLines || []).forEach(line => {
    const amt = Number(line.amount) || 0;
    const pcts = line.paymentPct || [];
    for (let m = 0; m < H; m += 1) {
      const calIdx = (moisDebut + m) % 12;
      const p = Number(pcts[calIdx]) || 0;
      decaisseOpening[m] += amt * p / 100;
    }
  });

  const decaisseExploitTTC = months.map(m => decaisseExploitHT[m] + decaisseExploitTVA[m] + decaisseOpening[m]);

  // 3. SALAIRES & PERSONNEL — délégué au module calcSalaires
  const sal = calcSalaires(s, H);
  const salaireBrutMensuel = sal.salaireBrutMensuel;
  const nbMois = sal.nbMois;
  const masseAnnuelle = salaireBrutMensuel * nbMois; // conservé pour compat (ailleurs dans le code)

  // Tableaux mensuels indexés sur l'horizon
  const masseAjustee = sal.brut;          // base + 13e + variable
  const masseBaseArr = sal.base;          // base récurrente uniquement
  const masseVariableArr = sal.variable.map((v, m) => v + sal.treizieme[m]); // 13e + primes + jetons

  // Frais hors charges sociales — tableau mensuel selon mode + moisActifs
  const computeFraisMensuel = (montant, mode, moisActifs) => {
    const arr = Array(H).fill(0);
    const m_actifs = Array.isArray(moisActifs) ? moisActifs : [0,1,2,3,4,5,6,7,8,9,10,11];
    if (m_actifs.length === 0 || !montant) return arr;
    if (mode === 'annuel') {
      const parMois = montant / m_actifs.length;
      m_actifs.forEach(idx => { if (idx < H) arr[idx] = parMois; });
    } else {
      m_actifs.forEach(idx => { if (idx < H) arr[idx] = montant; });
    }
    return arr;
  };
  const fraisForfaitairesArr = computeFraisMensuel(Number(s.fraisForfaitaires) || 0, s.fraisForfaitairesMode || 'mensuel', s.fraisForfaitairesMoisActifs);
  const formationsArr = computeFraisMensuel(Number(s.formations) || 0, s.formationsMode || 'mensuel', s.formationsMoisActifs);
  const autresFraisArr = computeFraisMensuel(Number(s.autresFrais) || 0, s.autresFraisMode || 'mensuel', s.autresFraisMoisActifs);
  const indemnitesArr = computeFraisMensuel(Number(s.indemnites) || 0, s.indemnitesMode || 'mensuel', s.indemnitesMoisActifs);

  // ═══════════════════════════════════════════════════════════════════
  // CHARGES SOCIALES — base de calcul UNIQUE = masseAjustee (brut + primes)
  // Tous les taux (cotisations base + assurances) s'appliquent sur cette base.
  // Plus de "masse déclarée" : on travaille sur la masse réellement versée.
  // ═══════════════════════════════════════════════════════════════════

  // 1. Cotisations de base (AVS / AC / AF / LFP / ...)
  const chargesEmployeurMensuel = months.map(m => {
    const masse = masseAjustee[m];
    return (s.tauxCharges || []).reduce((acc, t) => {
      const base = t.plafond ? Math.min(masse, t.plafond / 12) : masse;
      return acc + base * ((Number(t.employeur) || 0) / 100);
    }, 0);
  });
  const chargesEmployeMensuel = months.map(m => {
    const masse = masseAjustee[m];
    return (s.tauxCharges || []).reduce((acc, t) => {
      const base = t.plafond ? Math.min(masse, t.plafond / 12) : masse;
      return acc + base * ((Number(t.employe) || 0) / 100);
    }, 0);
  });

  // 2. Assurances suppl. & LPP — un seul taux saisi par l'utilisateur (tauxEstimatif).
  // Charge constatée mensuelle = taux × masse réelle versée du mois × part.
  // Décaissement = total annuel réparti sur les mois de paiement cochés.
  const assurChargeEmployeur = Array(H).fill(0);
  const assurChargeEmploye = Array(H).fill(0);
  const assurDecaissement = Array(H).fill(0);

  (s.assurancesPersonnes || []).forEach(a => {
    const partEmpr = (Number(a.partEmployeur) || 0) / 100;
    const partEmpl = (Number(a.partEmploye) || 0) / 100;
    const tauxPct = (Number(a.tauxEstimatif) || 0) / 100;
    if (tauxPct === 0) return; // pas de taux = assurance ignorée
    const moisPaie = Array.isArray(a.moisPaiements) && a.moisPaiements.length > 0
      ? a.moisPaiements
      : [0];

    months.forEach(m => {
      const baseMensuelle = masseAjustee[m] * tauxPct;
      assurChargeEmployeur[m] += baseMensuelle * partEmpr;
      assurChargeEmploye[m] += baseMensuelle * partEmpl;
    });

    // Décaissement aux mois cochés : total annuel sur masse versée /nbPaiements
    const totalAnnuel = sal.sumBrut * tauxPct;
    const parPaiement = totalAnnuel / moisPaie.length;
    moisPaie.forEach(moisCal => {
      const idx = calToHorizon(moisCal, sal.moisDebut, H);
      if (idx !== null) assurDecaissement[idx] += parPaiement;
    });
  });

  // 3. Salaire net versé = masseAjustee − cotisations base empl − assurances empl
  const salairesNets = months.map(m =>
    masseAjustee[m] - chargesEmployeMensuel[m] - assurChargeEmploye[m]
  );

  // 4. Décaissements personnel (cash réellement sorti) :
  //    - net versé à l'employé
  //    - cotisations base décaissées chaque mois (empr + empl reversés à la caisse)
  //    - assurances décaissées selon mois cochés
  const decaissementChargesSociales = months.map(m => chargesEmployeurMensuel[m] + chargesEmployeMensuel[m]);

  const decaissePers = months.map(m =>
    salairesNets[m] + decaissementChargesSociales[m] + assurDecaissement[m] + fraisForfaitairesArr[m] + formationsArr[m] + autresFraisArr[m] - indemnitesArr[m]
  );

  // 4. TVA
  // base : 'convenues' (sur facturé) | 'recues' (sur encaissé)
  // méthode : 'effective' (collectée − déductible) | 'tdfn' (taux unique × TTC)
  const baseTva = s.baseTva || 'convenues';
  const methode = s.methodeTva || 'effective';
  const tvaTdfnPct = (Number(s.tauxTvaTdfn) || 0) / 100;
  const tdfnByMonth = Array.isArray(s.tauxTvaTdfnPctByMonth) ? s.tauxTvaTdfnPctByMonth : null;
  const tvaTdfnRateAt = (m) => (tdfnByMonth ? (Number(tdfnByMonth[m]) || 0) / 100 : tvaTdfnPct);

  // Assiette mensuelle selon la base
  const assietteTva = months.map(m => {
    if (baseTva === 'recues') return encaisseHT[m]; // CA encaissé HT
    // convenues : CA facturé HT (valeurs brutes avant délai)
    return (s.revenus || []).reduce((acc, l) =>
      l.soumisTva && Array.isArray(l.valeurs) ? acc + (Number(l.valeurs[m]) || 0) : acc, 0);
  });

  const tvaCollectee = months.map(m =>
    methode === 'tdfn'
      ? (assietteTva[m] * (1 + tvaRateAt(m))) * tvaTdfnRateAt(m)  // TTC × taux TDFN
      : assietteTva[m] * tvaRateAt(m)                             // HT × taux TVA
  );

  const tvaDeductible = months.map(m =>
    methode === 'tdfn' ? 0 : variables.tva[m] + mensuel.tva[m] + specifique.tva[m]
  );

  const tvaDecaisse = Array(H).fill(0);
  const tailleperiode = s.periodiciteTva === 'mensuelle' ? 1 : s.periodiciteTva === 'trimestrielle' ? 3 : 6;
  for (let pStart = 0; pStart < H; pStart += tailleperiode) {
    const pEnd = Math.min(pStart + tailleperiode, H);
    let tvaDue = 0;
    for (let m = pStart; m < pEnd; m++) tvaDue += tvaCollectee[m] - tvaDeductible[m];
    const moisPaiement = pEnd - 1 + (Number(s.decalagePaiementTva) ?? 2);
    if (moisPaiement < H && tvaDue > 0) tvaDecaisse[moisPaiement] += tvaDue;
  }

  // 5. SYNTHÈSE
  const syntheseMois = months.map(m => {
    const enc = encaisseTTC[m];
    const decExp = decaisseExploitTTC[m];
    const decPers = decaissePers[m];
    const decTva = tvaDecaisse[m];
    const totalDec = decExp + decPers + decTva;
    return {
      m,
      encHT: encaisseHT[m],
      encTVA: encaisseTVA[m],
      encOpening: encaisseOpening[m],
      enc,
      decExpHT: decaisseExploitHT[m],
      decOpening: decaisseOpening[m],
      decExp,
      decPers,
      decTva,
      totalDec,
      fluxNet: enc - totalDec,
      decVariables: variables.ht[m] + variables.tva[m],
      decMensuel: mensuel.ht[m] + mensuel.tva[m],
      decSpecifique: specifique.ht[m] + specifique.tva[m],
      decVariablesHT: variables.ht[m],
      decMensuelHT: mensuel.ht[m],
      decSpecifiqueHT: specifique.ht[m],
      decVariablesTVA: variables.tva[m],
      decMensuelTVA: mensuel.tva[m],
      decSpecifiqueTVA: specifique.tva[m],
      decExpTVA: decaisseExploitTVA[m],
      tvaCollecteeAFC: tvaCollectee[m],
      tvaDeductibleAFC: tvaDeductible[m],
      decAssurances: assurDecaissement[m],
      // Détails personnel
      persSalairesNets: salairesNets[m],
      persChargesSociales: decaissementChargesSociales[m],
      persImpotSource: 0,
      persAssurances: assurDecaissement[m],
      persFraisForfait: fraisForfaitairesArr[m],
      persFormations: formationsArr[m],
      persAutresFrais: autresFraisArr[m],
      persIndemnites: -indemnitesArr[m],
      persMasseBase: masseBaseArr[m],
      persMasseVariable: masseVariableArr[m],
      // Décomposition fine — module calcSalaires
      persMasseTreizieme: sal.treizieme[m],
      persMassePrimes: sal.primes[m],
      persMasseJetons: sal.jetons[m],
      // Charges sociales DUES (constatées) — indépendantes du moment de décaissement
      persChargesSocialesDues: chargesEmployeurMensuel[m] + chargesEmployeMensuel[m],
      persAssurancesDues: assurChargeEmployeur[m] + assurChargeEmploye[m],
      persChargesEmployeurDues: chargesEmployeurMensuel[m] + assurChargeEmployeur[m],
      persChargesEmployeDues: chargesEmployeMensuel[m] + assurChargeEmploye[m],
    };
  });

  let solde = Number(s.soldeInitial) || 0;
  const soldes = syntheseMois.map(sm => {
    const debut = solde;
    solde = solde + sm.fluxNet;
    return { ...sm, soldeDebut: debut, soldeFin: solde };
  });

  const totalEnc = soldes.reduce((a, b) => a + b.enc, 0);
  const totalDec = soldes.reduce((a, b) => a + b.totalDec, 0);
  const soldeMin = Math.min(...soldes.map(s => s.soldeFin));
  const soldeMinMois = soldes.find(s => s.soldeFin === soldeMin)?.m;
  const burnRate = totalDec / H;

  // Salaire net mensuel pour vérification (un mois normal)
  const salaireNetVerifMois = salaireBrutMensuel - (chargesEmployeMensuel[0] || 0) - (assurChargeEmploye[0] || 0);
  const totalChargesEmployeurAnn = sum(chargesEmployeurMensuel) + sum(assurChargeEmployeur);
  const totalChargesEmployeAnn = sum(chargesEmployeMensuel) + sum(assurChargeEmploye);
  const fraisForfaitairesAn = sum(fraisForfaitairesArr);
  const coutTotalEntrepriseAn = sum(masseAjustee) + totalChargesEmployeurAnn + fraisForfaitairesAn;

  // Décompose les périodes TVA (pour la table de réconciliation)
  const periodesTva = [];
  for (let pStart = 0; pStart < H; pStart += tailleperiode) {
    const pEnd = Math.min(pStart + tailleperiode, H);
    let coll = 0, ded = 0;
    for (let m = pStart; m < pEnd; m++) { coll += tvaCollectee[m]; ded += tvaDeductible[m]; }
    const moisPaiement = pEnd - 1 + (Number(s.decalagePaiementTva) ?? 2);
    periodesTva.push({
      pStart, pEnd: pEnd - 1, moisPaiement,
      collectee: coll, deductible: ded, solde: coll - ded,
      paye: moisPaiement < H && (coll - ded) > 0,
    });
  }

  // Report personnel : charges DUES non encore décaissées sur l'horizon
  // (hors salaires nets et frais forfaitaires/indemnités/formations qui sont mensuels)
  const cumulChargesSocialesDues = months.reduce((a, m) => a + (chargesEmployeurMensuel[m] + chargesEmployeMensuel[m]), 0);
  const cumulChargesSocialesDec = months.reduce((a, m) => a + decaissementChargesSociales[m], 0);
  const cumulAssurancesDues = months.reduce((a, m) => a + (assurChargeEmployeur[m] + assurChargeEmploye[m]), 0);
  const cumulAssurancesDec = months.reduce((a, m) => a + assurDecaissement[m], 0);
  const reportChargesSociales = Math.max(0, cumulChargesSocialesDues - cumulChargesSocialesDec);
  const reportAssurances = Math.max(0, cumulAssurancesDues - cumulAssurancesDec);
  const reportPersonnelTotal = reportChargesSociales + reportAssurances;

  return {
    horizon: H, soldes, masseAjustee, masseAnnuelle,
    chargesEmployeurMensuel, chargesEmployeMensuel,
    salairesNets, salaireNetVerifMois,
    totalChargesEmployeurAnn, totalChargesEmployeAnn,
    fraisForfaitairesAn, coutTotalEntrepriseAn,
    assurDecaissement,
    encaisseHT, encaisseTVA, encaisseTTC,
    tvaDecaisse, tvaCollectee, tvaDeductible, assietteTva,
    periodesTva, periodiciteTva: s.periodiciteTva || 'trimestrielle',
    decalagePaiementTva: Number(s.decalagePaiementTva) ?? 2,
    baseTva, methodeTva: methode,
    reportChargesSociales, reportAssurances, reportPersonnelTotal,
    moisLabels: sal.moisLabels,
    sal,
    kpi: {
      totalEnc, totalDec, fluxNetTotal: totalEnc - totalDec,
      soldeMin, soldeMinMois, burnRate,
      runway: burnRate > 0 ? (Number(s.soldeInitial) || 0) / burnRate : 0,
      soldeFinal: soldes[H - 1]?.soldeFin || (Number(s.soldeInitial) || 0),
    },
  };
}
